/**
 * useCameraRefusal — une caméra refusée en séance, vue sur la demande elle-même.
 *
 * L'écran lit l'accès à la caméra (`useMediaPermissions`) pour poser le point du bouton et le dire dans
 * « Un problème ? ». Une lecture impossible (Firefox ne connaît pas toujours la permission `camera`) dit
 * « on ne sait pas » — à raison : l'ancien y marquait une caméra qui marchait. Mais une caméra vraiment
 * refusée ne se disait alors nulle part. Le refus se lit donc aussi là où il se produit : l'échec d'une
 * demande de la caméra, celle de la salle (sa connexion, `mediaDevicesError` de LiveKit) ou le clic de
 * l'élève, classé par la règle du test d'entrée (`micBlockerOf`). La caméra ouverte l'efface.
 *
 * Pas de boîte : la caméra n'est pas ce que le masteur attend pour parler (le micro a la sienne).
 */
import { type LocalParticipant } from "livekit-client"
import { useCallback, useEffect, useState } from "react"

import { type EngineTelemetry } from "../../shared/telemetry"
import { micBlockerOf, stickyBlocker, type MicBlocker } from "./micRefusal"
import { useMediaDevicesError } from "./useMediaDevicesError"

export type CameraRefusal = {
    /** Qui refuse la caméra, tant qu'elle n'est pas ouverte ; `null` : aucun refus vu. */
    blocker: MicBlocker | null
    /** L'échec d'une demande de la caméra : seul un refus compte. Rend `true` s'il en était un. */
    report: (cause: unknown) => boolean
}

export function useCameraRefusal(options: {
    localParticipant: Pick<LocalParticipant, "on" | "off">
    cameraOn: boolean
    telemetry: EngineTelemetry
}): CameraRefusal {
    const { localParticipant, cameraOn, telemetry } = options
    const [blocker, setBlocker] = useState<MicBlocker | null>(null)

    const report = useCallback(
        (cause: unknown) => {
            const next = micBlockerOf(cause)
            if (next == null) return false
            telemetry.transition("camera_ok", "camera_refused", next)
            setBlocker((current) => stickyBlocker(current, next))
            return true
        },
        [telemetry]
    )

    useMediaDevicesError(localParticipant, "videoinput", report)

    useEffect(() => {
        if (cameraOn && blocker != null) setBlocker(null)
    }, [cameraOn, blocker])

    return { blocker: cameraOn ? null : blocker, report }
}
